/**
 * Abonnés annuels et mensuels — aplats empilés, deux séries.
 *
 * Les deux seules séries du dashboard à partager un graphique, et c'est
 * légitime : elles sont sur la même échelle et leur somme fait un tout — le
 * nombre total d'abonnés payants. Part-à-tout dans le temps, donc empilement.
 *
 * C'est aussi le seul endroit où l'emplacement 2 apparaît. Les deux teintes
 * sont validées ensemble pour la surface sombre (ΔE CVD 11,2 ; vision normale
 * 35,1), et la légende double la couleur par du texte : l'identité ne repose
 * jamais sur la seule couleur.
 *
 * Les mensuels ne sont pas stockés : ils se déduisent de
 * `total_subscribers - total_annual_subscribers`, calculé côté API.
 */

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatCompact, formatDayLong, formatDayShort, formatInteger } from '../lib/format';
import type { NullableAccountsPoint } from '../lib/series';
import { AREA_FILL_OPACITY, AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

const legend: readonly LegendEntry[] = [
  { label: 'Annuels', color: COLORS.series1, shape: 'area' },
  { label: 'Mensuels', color: COLORS.series2, shape: 'area' },
];

const dash = (value: number | null) => (value === null ? '—' : formatInteger(value));

const columns: ReadonlyArray<DataColumn<NullableAccountsPoint>> = [
  { key: 'date', header: 'Jour', render: (row) => formatDayLong(row.date) },
  {
    key: 'annual',
    header: 'Annuels',
    numeric: true,
    render: (row) => dash(row.annualSubscribers),
  },
  {
    key: 'monthly',
    header: 'Mensuels',
    numeric: true,
    render: (row) => dash(row.monthlySubscribers),
  },
  {
    key: 'total',
    header: 'Total',
    numeric: true,
    render: (row) => dash(row.totalSubscribers),
  },
];

/**
 * Le verdict porte sur le TOTAL des abonnés, pas sur l'une des deux bandes :
 * un marqueur qui pousse les mensuels vers l'annuel ne change pas le nombre
 * d'abonnés, et deux verdicts contraires sur le même trait ne se lisent pas.
 *
 * Mode `growth`, comme les comptes : c'est un cumul, ce qui se compare est la
 * pente — les abonnés nets gagnés par jour.
 */
const MARKER_CONFIG = {
  value: (row: NullableAccountsPoint) => row.totalSubscribers,
  mode: 'growth',
  unit: 'count',
  higherIsBetter: true,
  scope: 'analytics',
} as const;

export function SubscribersChart({ data }: { data: readonly NullableAccountsPoint[] }) {
  const markers = useChartMarkers(data, MARKER_CONFIG);

  return (
    <ChartCard
      title="Abonnés payants"
      subtitle="Annuels et mensuels empilés — leur somme fait le total des abonnés"
      emptyMessage="Aucun snapshot sur cette période. Le premier arrive au lendemain du premier sync."
      rows={data}
      columns={columns}
      footer={<MarkerStrip items={markers.items} />}
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="88%">
        <AreaChart data={data as NullableAccountsPoint[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip labelFormatter={formatDayLong} valueFormatter={formatInteger} />
            }
          />
          {markers.lines}
          {/* Le contour de 2 px dans la teinte de la serie separe les deux
              bandes : le lecteur voit la frontiere sans qu'on dessine un
              cadre autour de l'aplat. */}
          <Area
            isAnimationActive={false}
            type="monotone"
            stackId="subs"
            dataKey="annualSubscribers"
            name="Annuels"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            fill={COLORS.series1}
            fillOpacity={AREA_FILL_OPACITY}
            dot={false}
            connectNulls={false}
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
          <Area
            isAnimationActive={false}
            type="monotone"
            stackId="subs"
            dataKey="monthlySubscribers"
            name="Mensuels"
            stroke={COLORS.series2}
            strokeWidth={LINE_WIDTH}
            fill={COLORS.series2}
            fillOpacity={AREA_FILL_OPACITY}
            dot={false}
            connectNulls={false}
            activeDot={{ r: 4, fill: COLORS.series2, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
